import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { CollectiblesService } from '../../../services/collectiblesService';
import marketplaceService from '../../../services/marketplaceService';

const CreateAuctionModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [collectibles, setCollectibles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listingType, setListingType] = useState('regular'); // 'regular' or 'premiere'
  const [formData, setFormData] = useState({
    collectible_id: '',
    title: '',
    description: '',
    starting_bid: '',
    reserve_price: '',
    bid_increment: '10.00',
    currency: 'USD',
    condition: '',
    auction_type: 'regular',
    category_id: '',
    start_date: '',
    end_date: '',
    shipping_cost: '0',
    shipping_details: {}
  });

  useEffect(() => {
    if (isOpen) {
      loadUserCollectibles();
      loadAuctionCategories();
    }
  }, [isOpen]);

  const loadUserCollectibles = async () => {
    const { data } = await CollectiblesService?.getUserCollectibles(user?.id);
    setCollectibles(data || []);
  };

  const loadAuctionCategories = async () => {
    const { data } = await marketplaceService?.getAuctionCategories();
    setCategories(data || []);
  };

  const handleCollectibleSelect = (collectibleId) => {
    const selected = collectibles?.find(c => c?.id === collectibleId);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        collectible_id: collectibleId,
        title: selected?.name || '',
        description: selected?.description || '',
        starting_bid: selected?.current_value || '',
        condition: selected?.condition || '',
        featured_image_url: selected?.primary_image_url || ''
      }));

      // Auto-select category based on collectible type
      const matchingCategory = categories?.find(c => c?.collectible_type === selected?.collectible_type);
      if (matchingCategory && listingType === 'premiere') {
        setFormData(prev => ({ ...prev, category_id: matchingCategory?.id }));
      }
    }
  };

  const handleListingTypeChange = (type) => {
    setListingType(type);
    setFormData(prev => ({
      ...prev,
      auction_type: type,
      category_id: type === 'premiere' ? prev?.category_id : ''
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    const auctionData = {
      ...formData,
      seller_id: user?.id,
      starting_bid: parseFloat(formData?.starting_bid),
      reserve_price: formData?.reserve_price ? parseFloat(formData?.reserve_price) : null,
      bid_increment: parseFloat(formData?.bid_increment || 10),
      shipping_cost: parseFloat(formData?.shipping_cost || 0),
      start_date: new Date(formData?.start_date)?.toISOString(),
      end_date: new Date(formData?.end_date)?.toISOString(),
      status: new Date(formData?.start_date) <= new Date() ? 'active' : 'scheduled'
    };

    const { data, error } = await marketplaceService?.createAuction(auctionData);
    
    if (!error) {
      onSuccess?.();
    } else {
      alert('Failed to create auction: ' + error);
    }
    
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Gavel" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-card-foreground">Create Auction</h2>
              <p className="text-sm text-muted-foreground">List your collectible for auction</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Listing Type Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Auction Type <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleListingTypeChange('regular')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  listingType === 'regular' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    listingType === 'regular' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name="Clock" size={20} />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-foreground">Regular Auction</p>
                    <p className="text-xs text-muted-foreground">Standard timed auction</p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleListingTypeChange('premiere')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  listingType === 'premiere' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    listingType === 'premiere' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name="Star" size={20} />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-foreground">Premiere Auction</p>
                    <p className="text-xs text-muted-foreground">Monthly featured event</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Select Collectible */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Select Collectible <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData?.collectible_id}
              onChange={(e) => handleCollectibleSelect(e?.target?.value)}
              required
            >
              <option value="">Choose a collectible...</option>
              {collectibles?.map(item => (
                <option key={item?.id} value={item?.id}>
                  {item?.name} - {item?.collectible_type}
                </option>
              ))}
            </Select>
          </div>

          {/* Premiere Category (only for premiere auctions) */}
          {listingType === 'premiere' && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Premiere Category <span className="text-destructive">*</span>
              </label>
              <Select
                value={formData?.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e?.target?.value }))}
                required={listingType === 'premiere'}
              >
                <option value="">Select category...</option>
                {categories?.map(cat => (
                  <option key={cat?.id} value={cat?.id}>
                    {cat?.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Your item will be featured in the monthly premiere auction for this category
              </p>
            </div>
          )}

          {/* Title */}
          <Input
            label="Auction Title"
            required
            value={formData?.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
            placeholder="Enter auction title"
          />

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              required
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              placeholder="Describe your item, its condition, provenance, and any important details"
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Condition */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Condition <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData?.condition}
              onChange={(e) => setFormData(prev => ({ ...prev, condition: e?.target?.value }))}
              required
            >
              <option value="">Select condition...</option>
              <option value="mint">Mint</option>
              <option value="near-mint">Near Mint</option>
              <option value="excellent">Excellent</option>
              <option value="very-good">Very Good</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
            </Select>
          </div>

          {/* Bidding Details */}
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Starting Bid"
              type="number"
              step="0.01"
              required
              value={formData?.starting_bid}
              onChange={(e) => setFormData(prev => ({ ...prev, starting_bid: e?.target?.value }))}
              placeholder="0.00"
            />
            <Input
              label="Reserve Price (Optional)"
              type="number"
              step="0.01"
              value={formData?.reserve_price}
              onChange={(e) => setFormData(prev => ({ ...prev, reserve_price: e?.target?.value }))}
              placeholder="0.00"
            />
            <Input
              label="Bid Increment"
              type="number"
              step="0.01"
              required
              value={formData?.bid_increment}
              onChange={(e) => setFormData(prev => ({ ...prev, bid_increment: e?.target?.value }))}
              placeholder="10.00"
            />
          </div>

          {/* Auction Dates */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date & Time"
              type="datetime-local"
              required
              value={formData?.start_date}
              onChange={(e) => setFormData(prev => ({ ...prev, start_date: e?.target?.value }))}
            />
            <Input
              label="End Date & Time"
              type="datetime-local"
              required
              value={formData?.end_date}
              onChange={(e) => setFormData(prev => ({ ...prev, end_date: e?.target?.value }))}
            />
          </div>

          {/* Shipping Cost */}
          <Input
            label="Shipping Cost"
            type="number"
            step="0.01"
            value={formData?.shipping_cost}
            onChange={(e) => setFormData(prev => ({ ...prev, shipping_cost: e?.target?.value }))}
            placeholder="0.00"
          />

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              iconName="Gavel"
              iconPosition="left"
              fullWidth
            >
              Create Auction
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAuctionModal;